/**
 * Availability inside each SKU's own lead time, read off the same walk Closing Stock uses. One
 * grid of period ends can't express "each SKU up to its own lead time", so this projects each
 * `{sku, warehouse}` at a period end of its own over `today → today + leadtime`.
 */
import { type IStockOutTimesDetails } from "~/models/stockOutTimes"

import { derivePeriodComponents, projectKeyComponents } from "./engine"
import type { LeadTimeAvailability } from "./types"

/** Key into the returned map; mirrors the loader's composite key. */
export const leadTimeKey = (skuId: string, warehouseId: string) => `${skuId}-&&&-${warehouseId}`

/**
 * `todayShift` is today's offset in fractional weeks from the current Monday — the same anchor
 * the stockout engine walks from. `leadtime` on each row is in days.
 */
export function deriveLeadTimeAvailability(
  details: IStockOutTimesDetails,
  todayShift: number,
): Map<string, LeadTimeAvailability> {
  const out = new Map<string, LeadTimeAvailability>()

  for (const entry of details) {
    // No window, no demand to be a share of.
    if (entry.leadtime <= 0) continue

    // `getForecastsForWeeks` extrapolates the final weekly step forever past the last week, so an
    // uncapped window invents demand the forecast never stated — a 20-week lead time against a
    // 5-week forecast would report four weeks of fabricated lost sales.
    const weeksOfForecastLeft = Math.max(entry.forecasts.length - todayShift, 0)
    const windowWeeks = Math.min(entry.leadtime / 7, weeksOfForecastLeft)
    if (windowWeeks <= 0) continue
    const windowEnd = todayShift + windowWeeks

    const { components } = projectKeyComponents(
      {
        forecasts: entry.forecasts,
        incomingOrders: entry.incomingOrders ?? [],
        forecastedReturns: entry.forecastedReturns ?? [],
        opening: entry.stockLevel ?? 0,
        salesWeekToDate: entry.salesWeekToDate ?? undefined,
        hasSkuAdjustedDemand: false,
        keyId: leadTimeKey(entry.skuId, entry.warehouseId),
      },
      [windowEnd],
      todayShift,
    )

    const first = components[0]
    if (!first) continue
    const period = derivePeriodComponents(first, undefined)
    out.set(leadTimeKey(entry.skuId, entry.warehouseId), {
      demand: period.demand,
      // Lost sales is a share of demand, so keep it in [0, demand].
      lostSales: Math.min(Math.max(period.lostSales, 0), period.demand),
      // `min` so an uncapped window reports the SKU's lead time verbatim, not a float round-trip.
      windowDays: Math.min(entry.leadtime, windowWeeks * 7),
    })
  }

  return out
}

/**
 * Regroups the flat `{sku}-&&&-{warehouse}` map into the per-SKU shape `readLeadTimeAvailability`
 * takes: one entry per SKU, keyed by warehouse.
 */
export function groupLeadTimeBySku(
  byKey: Map<string, LeadTimeAvailability>,
): Map<string, Record<string, LeadTimeAvailability>> {
  const out = new Map<string, Record<string, LeadTimeAvailability>>()
  for (const [key, value] of byKey) {
    const [skuId = "", warehouseId = ""] = key.split("-&&&-")
    const entry = out.get(skuId) ?? {}
    entry[warehouseId] = value
    out.set(skuId, entry)
  }
  return out
}

/**
 * One SKU's residual, summed across its warehouses or narrowed to the selected one. Warehouses the
 * projection never reached contribute nothing.
 */
export function readLeadTimeAvailability(
  byWarehouse: Record<string, LeadTimeAvailability | undefined> | undefined,
  warehouseId: string | undefined,
): LeadTimeAvailability {
  let demand = 0
  let lostSales = 0
  let windowDays = 0
  for (const [wh, pair] of Object.entries(byWarehouse ?? {})) {
    if (warehouseId !== undefined && wh !== warehouseId) continue
    demand += pair?.demand ?? 0
    lostSales += pair?.lostSales ?? 0
    windowDays = Math.max(windowDays, pair?.windowDays ?? 0)
  }
  return { demand, lostSales, windowDays }
}

/**
 * Field-wise sum across the SKUs of one row, for a grain that covers more than one. Rows must go
 * through this rather than a hand-rolled loop, or they divide by a different window than
 * `readLeadTimeAvailability` and the two grains disagree. `windowDays` takes the widest window.
 */
export function sumLeadTimeAvailability(parts: LeadTimeAvailability[]): LeadTimeAvailability {
  let demand = 0
  let lostSales = 0
  let windowDays = 0
  for (const p of parts) {
    demand += p.demand
    lostSales += p.lostSales
    windowDays = Math.max(windowDays, p.windowDays)
  }
  return { demand, lostSales, windowDays }
}
